import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useNavigate } from "react-router-dom";

export const HeroSection = () => {
  const { t, getLocalizedPath } = useLanguage();
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<0 | 1>(0);
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video1 = video1Ref.current;
    const video2 = video2Ref.current;

    const handleVideo1End = () => {
      setActiveVideo(1);
      video2?.play();
    };

    const handleVideo2End = () => {
      setActiveVideo(0);
      video1?.play();
    };

    video1?.addEventListener("ended", handleVideo1End);
    video2?.addEventListener("ended", handleVideo2End);

    return () => {
      video1?.removeEventListener("ended", handleVideo1End);
      video2?.removeEventListener("ended", handleVideo2End);
    };
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="min-h-screen flex items-center pt-20 lg:pt-0">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left: Text Content */}
          <div className="order-2 lg:order-1 space-y-6 lg:space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-3"
            >
              <span className="text-sm font-medium text-muted-foreground tracking-wide uppercase">{t.hero.badge}</span>
              <span className="h-px w-12 bg-gradient-to-r from-primary to-secondary" />
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight text-foreground"
            >
              {t.hero.headline} <span className="text-primary">{t.hero.headlineHighlight1}</span> {t.hero.headlineIn}{" "}
              <span className="text-primary">{t.hero.headlineHighlight2}</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-lg lg:text-xl text-muted-foreground font-light max-w-xl leading-relaxed"
            >
              {t.hero.subheadline} <strong className="text-foreground">{t.hero.subheadlineStrong1}</strong>{t.hero.subheadlineReduces}{" "}
              <strong className="text-foreground">{t.hero.subheadlineStrong2}</strong> {t.hero.subheadlineGuarantees}{" "}
              <strong className="text-foreground">{t.hero.subheadlineStrong3}</strong> {t.hero.subheadlineImmediate}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 pt-4"
            >
              <Button
                size="lg"
                onClick={() => navigate(getLocalizedPath("/contact"))}
                className="bg-primary hover:bg-primary/90 text-lg px-8 py-6 shadow-glow-blue group"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate(getLocalizedPath("/rental"))}
                className="text-lg px-8 py-6 border-border hover:border-primary hover:bg-primary/5"
              >
                {t.hero.ctaSecondary}
              </Button>
            </motion.div>
          </div>

          {/* Right: Video Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative aspect-video rounded-3xl overflow-hidden bg-muted group">
              {/* Video 1 - Hoooly */}
              <video
                ref={video1Ref}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                  activeVideo === 0 ? "opacity-100" : "opacity-0"
                }`}
                autoPlay
                muted
                playsInline
                preload="auto"
              >
                <source src="/videos/ganiga_hoooly_sito_v1.mp4" type="video/mp4" />
              </video>

              {/* Video 2 - Small */}
              <video
                ref={video2Ref}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                  activeVideo === 1 ? "opacity-100" : "opacity-0"
                }`}
                muted
                playsInline
                preload="none"
              >
                <source src="/videos/ganiga_small_sito_v1.mp4" type="video/mp4" />
              </video>

              {/* Decorative Elements */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl pointer-events-none" />
            </div>

            {/* Video Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              <span
                className={`w-2 h-2 rounded-full transition-all duration-500 ${
                  activeVideo === 0 ? "bg-primary w-6" : "bg-foreground/30"
                }`}
              />
              <span
                className={`w-2 h-2 rounded-full transition-all duration-500 ${
                  activeVideo === 1 ? "bg-primary w-6" : "bg-foreground/30"
                }`}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
